import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  Multiselect,
  Select,
  SpaceBetween,
  Spinner,
  Table,
} from '@cloudscape-design/components';
import { NexusMappingJobService } from '../services/nexus-api';
import { useMappingJobsPolling } from '../hooks/useMappingJobPolling';
import { getUserAlias } from '../auth/midway';
import { getDataService } from '../services/dataServiceFactory';

const statusVariant = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'IN_PROGRESS':
      return 'blue';
    case 'QUEUED':
    default:
      return 'grey';
  }
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const DEFAULT_SOURCE = {
  label: 'AWS Control Catalog (1.0)',
  value: 'AWS.ControlCatalog#1.0',
};

const MappingJobs = () => {
  const serviceRef = useRef(new NexusMappingJobService());
  const service = serviceRef.current;
  const enabled = service.isEnabled();

  const {
    jobs,
    loading: pollingLoading,
    error: pollingError,
    refresh,
  } = useMappingJobsPolling(service, { enabled });

  const [showModal, setShowModal] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(false);
  const [frameworksError, setFrameworksError] = useState(null);
  const [sourceFramework, setSourceFramework] = useState(DEFAULT_SOURCE);
  const [targetFrameworks, setTargetFrameworks] = useState([]);
  const [topK, setTopK] = useState('50');
  const [threshold, setThreshold] = useState('0.8');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [frameworksLoaded, setFrameworksLoaded] = useState(false);

  useEffect(() => {
    if (!showModal || frameworksLoaded) return;
    let cancelled = false;
    setFrameworksLoading(true);
    setFrameworksError(null);
    getDataService()
      .getAvailableFrameworks()
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res) ? res : res?.frameworks || [];
        setFrameworks(
          items.map((f) => ({
            label: `${f.frameworkName} (${f.version})`,
            value: `${f.frameworkName}#${f.version}`,
          })),
        );
        setFrameworksLoaded(true);
      })
      .catch((err) => {
        if (!cancelled) setFrameworksError(err);
      })
      .finally(() => {
        if (!cancelled) setFrameworksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showModal, frameworksLoaded]);

  const columnDefs = useMemo(
    () => [
      {
        id: 'status',
        header: 'Status',
        cell: (item) => (
          <Badge color={statusVariant(item.status)}>{item.status}</Badge>
        ),
        width: 140,
      },
      {
        id: 'createdAt',
        header: 'Created',
        cell: (item) => formatDate(item.createdAt),
        sortingField: 'createdAt',
      },
      {
        id: 'source',
        header: 'Source',
        cell: (item) =>
          item.config?.sourceFramework || 'AWS.ControlCatalog#1.0',
      },
      {
        id: 'frameworks',
        header: 'Targets',
        cell: (item) =>
          (
            item.config?.targetFrameworks ??
            item.config?.selectedFrameworks ??
            []
          ).join(', ') || '-',
      },
      {
        id: 'requestedBy',
        header: 'Requested by',
        cell: (item) => item.requestedBy || '-',
      },
      {
        id: 'completedAt',
        header: 'Completed',
        cell: (item) => formatDate(item.completedAt),
      },
      {
        id: 'error',
        header: 'Error',
        cell: (item) => item.errorMessage || '',
      },
    ],
    [],
  );

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const requestedBy = (await getUserAlias()) || 'unknown';
      const sourceValue = sourceFramework?.value;
      const targets = targetFrameworks
        .map((t) => t.value)
        .filter((v) => v !== sourceValue);
      await service.createJob({
        sourceFramework: sourceValue,
        targetFrameworks: targets,
        awsControlMode: 'with_mappings',
        topK: Number(topK) || 50,
        threshold: Number(threshold) || 0.8,
        requestedBy,
      });
      setShowModal(false);
      setTargetFrameworks([]);
      setSourceFramework(DEFAULT_SOURCE);
      // refresh failures surface via pollingError; don't leak into submitError.
      refresh().catch(() => {});
    } catch (err) {
      setSubmitError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const dismissModal = () => {
    setShowModal(false);
    setSubmitError(null);
  };

  if (!enabled) {
    return (
      <Container header={<Header variant="h1">Mapping jobs</Header>}>
        <Alert type="info" header="Feature disabled">
          Mapping jobs are not yet available in this environment. The feature
          is currently enabled in Beta only.
        </Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h1"
            description="Start a new framework mapping run and monitor progress."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={refresh}>
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowModal(true)}
                >
                  New mapping run
                </Button>
              </SpaceBetween>
            }
          >
            Mapping jobs
          </Header>
        }
      >
        {pollingError && (
          <Alert type="error" header="Failed to load jobs">
            {pollingError.message}
          </Alert>
        )}
        <Table
          columnDefinitions={columnDefs}
          items={jobs}
          loading={pollingLoading && jobs.length === 0}
          loadingText="Loading jobs"
          trackBy="JobId"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No mapping jobs yet</b>
              <Box variant="p" padding={{ bottom: 's' }}>
                Click "New mapping run" to queue one.
              </Box>
            </Box>
          }
        />
      </Container>

      <Modal
        visible={showModal}
        onDismiss={dismissModal}
        header="Start a new mapping run"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={dismissModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submit}
                loading={submitting}
                disabled={
                  !sourceFramework || targetFrameworks.length === 0
                }
              >
                Start job
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {submitError && (
            <Alert type="error" header="Failed to start job">
              {submitError.message}
            </Alert>
          )}
          {frameworksError && (
            <Alert type="warning" header="Could not load frameworks">
              {frameworksError.message}
            </Alert>
          )}
          <FormField
            label="Source framework"
            description="Controls from this framework will be mapped against the targets."
          >
            {frameworksLoading ? (
              <Spinner />
            ) : (
              <Select
                selectedOption={sourceFramework}
                onChange={({ detail }) => {
                  setSourceFramework(detail.selectedOption);
                  setTargetFrameworks((prev) =>
                    prev.filter(
                      (t) => t.value !== detail.selectedOption?.value,
                    ),
                  );
                }}
                options={
                  frameworks.length ? frameworks : [DEFAULT_SOURCE]
                }
                placeholder="Choose a source framework"
                filteringType="auto"
              />
            )}
          </FormField>
          <FormField
            label="Target frameworks"
            description="Source controls will be mapped against every selected target framework."
          >
            {frameworksLoading ? (
              <Spinner />
            ) : (
              <Multiselect
                selectedOptions={targetFrameworks}
                onChange={({ detail }) =>
                  setTargetFrameworks(detail.selectedOptions)
                }
                options={frameworks.filter(
                  (f) => f.value !== sourceFramework?.value,
                )}
                placeholder="Choose target frameworks"
                filteringType="auto"
              />
            )}
          </FormField>
          <FormField label="Top K retriever candidates">
            <Input
              value={topK}
              onChange={({ detail }) => setTopK(detail.value)}
              type="number"
            />
          </FormField>
          <FormField label="Reranker threshold">
            <Input
              value={threshold}
              onChange={({ detail }) => setThreshold(detail.value)}
              type="number"
              step={0.05}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};

export default MappingJobs;
